import React, { useState } from "react";
import { useFormik } from "formik";
import { Row, Col, Button, Form } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const RunReportFilterForm = ({ setFilterData, loadCampaignOptions, shouldDisplayCampaignType = false }) => {
  const [selectedCampaignOption, setSelectedCampaignOption] = useState(null);

  const initialValues = {
    campaignId: "",
    status: "",
    fromDate: null,
    toDate: null,
    campaignType: ""
  };

  const formik = useFormik({
    initialValues,
    validate: (values) => {
      const errors = {};
      const { fromDate, toDate } = values;

      if (fromDate && toDate && fromDate > toDate) {
        errors.fromDate = "From Date cannot be later than To Date";
        errors.toDate = "To Date cannot be earlier than From Date";
      }

      return errors;
    },
    onSubmit: (values) => {
      const payload = { ...values };
      Object.keys(payload).forEach((key) => {
        if (!payload[key] && payload[key] !== 0) {
          delete payload[key];
        }
      });
      setFilterData(payload);
    },
  });

  const handleReset = () => {
    formik.resetForm();
    setSelectedCampaignOption(null); // Reset AsyncSelect value
    setFilterData("");
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="p-3 border rounded bg-light">
        {/* Filters Row */}
        <Row className="g-3 align-items-end">

          {/* Campaign Name */}
          <Col lg={4} md={6}>
            <Form.Group controlId="campaignName">
              <Form.Label className="fw-semibold">
                Campaign Name
              </Form.Label>
              <AsyncSelect
                cacheOptions
                defaultOptions={false}
                loadOptions={(inputValue) =>
                  inputValue.length >= 4
                    ? loadCampaignOptions(inputValue)
                    : Promise.resolve([])
                }
                value={selectedCampaignOption}
                onChange={(selected) => {
                  setSelectedCampaignOption(selected);
                  formik.setFieldValue("campaignId", selected?.value || "");
                }}
                placeholder="Type at least 4 characters"
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </Form.Group>
          </Col>

          {/* Campaign Channel */}
          {shouldDisplayCampaignType && (
            <Col lg={2} md={6}>
              <Form.Group controlId="campaignChannel">
                <Form.Label className="fw-semibold">
                  Select Channel
                </Form.Label>
                <Form.Select
                  name="campaignType"
                  value={formik.values.campaignType}
                  onChange={formik.handleChange}
                >
                  <option value="">Select Channel</option>
                  <option value="ivr">IVR</option>
                  <option value="ivr_marketing">IVR Marketing</option>
                </Form.Select>
              </Form.Group>
            </Col>
          )}

          {/* Status */}
          <Col lg={2} md={6}>
            <Form.Group controlId="status">
              <Form.Label className="fw-semibold">
                Status
              </Form.Label>
              <Form.Select
                name="status"
                value={formik.values.status}
                onChange={formik.handleChange}
              >
                <option value="">Select Status</option>
                <option value="pending">Pending</option>
                <option value="started">Started</option>
                <option value="completed">Completed</option>
                <option value="deleted">Deleted</option>
                <option value="failed">Failed</option>
              </Form.Select>
            </Form.Group>
          </Col>

          {/* From Date */}
          <Col lg={2} md={6}>
            <Form.Group controlId="fromDate">
              <Form.Label className="fw-semibold">
                From Date
              </Form.Label>
              <DatePicker
                selected={formik.values.fromDate}
                onChange={(date) => formik.setFieldValue("fromDate", date)}
                dateFormat="yyyy-MM-dd"
                className="form-control"
                placeholderText="Select From Date"
                maxDate={new Date()}
              />
              {formik.errors.fromDate && (
                <div className="text-danger small mt-1">
                  {formik.errors.fromDate}
                </div>
              )}
            </Form.Group>
          </Col>

          {/* To Date */}
          <Col lg={2} md={6}>
            <Form.Group controlId="toDate">
              <Form.Label className="fw-semibold">
                To Date
              </Form.Label>
              <DatePicker
                selected={formik.values.toDate}
                onChange={(date) => formik.setFieldValue("toDate", date)}
                dateFormat="yyyy-MM-dd"
                className="form-control"
                placeholderText="Select To Date"
                maxDate={new Date()}
              />
              {formik.errors.toDate && (
                <div className="text-danger small mt-1">
                  {formik.errors.toDate}
                </div>
              )}
            </Form.Group>
          </Col>

        </Row>

        {/* Buttons Row */}
        <Row className="mt-4">
          <Col className="d-flex justify-content-end gap-3">
            <Button
              onClick={handleReset}
              variant="outline-secondary"
              type="button"
              className="px-4"
            >
              Reset
            </Button>

            <Button
              type="submit"
              variant="primary"
              className="px-4 shadow-sm"
            >
              Filter
            </Button>
          </Col>
        </Row>
      </div>
    </form>
  );

};

export default RunReportFilterForm;
